const FLOWPLAYER_VIDEO_SELECTOR = '[data-flowplayer-video]';
const HLS_MIME_TYPE = 'application/vnd.apple.mpegurl';
const HOMEPAGE_HERO_MOBILE_MEDIA_QUERY = window.matchMedia('(max-width: 749px)');

/** @type {WeakSet<HTMLVideoElement>} */
const initializedVideos = new WeakSet();

/** @type {WeakMap<HTMLVideoElement, object>} */
const initializationRuns = new WeakMap();

/** @type {WeakMap<HTMLVideoElement, HlsInstance>} */
const hlsPlayers = new WeakMap();

/** @type {WeakMap<HTMLVideoElement, EventListener>} */
const nativePlaybackErrorHandlers = new WeakMap();

/** @type {Promise<HlsConstructor> | null} */
let hlsLibraryPromise = null;

/**
 * Load the vendored HLS playback engine only when native HLS is unavailable.
 *
 * @param {string} libraryUrl
 * @returns {Promise<HlsConstructor>}
 */
function loadHlsLibrary(libraryUrl) {
  if (window.Hls) {
    return Promise.resolve(window.Hls);
  }

  if (hlsLibraryPromise) {
    return hlsLibraryPromise;
  }

  hlsLibraryPromise = new Promise((resolve, reject) => {
    const handleLoad = () => {
      if (window.Hls) {
        resolve(window.Hls);
      } else {
        document.querySelector('script[data-flowplayer-hls-library]')?.remove();
        reject(new Error('The HLS playback engine did not initialize.'));
      }
    };
    const handleError = () => {
      document.querySelector('script[data-flowplayer-hls-library]')?.remove();
      reject(new Error('The HLS playback engine could not be loaded.'));
    };
    const existingScript = document.querySelector('script[data-flowplayer-hls-library]');

    if (existingScript instanceof HTMLScriptElement) {
      // A live request is already represented by hlsLibraryPromise. If there is
      // a script without a matching promise or Hls global, it is a stale failed
      // attempt whose load/error events will not fire again.
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = libraryUrl;
    script.async = true;
    script.dataset.flowplayerHlsLibrary = '';
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });

  hlsLibraryPromise.catch(() => {
    hlsLibraryPromise = null;
  });

  return hlsLibraryPromise;
}

/**
 * A configured mobile image replaces Flowplayer in the Homepage Hero at the
 * same breakpoint used by the component's responsive CSS.
 *
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
function isHomepageHeroMobileMediaActive(video) {
  return (
    HOMEPAGE_HERO_MOBILE_MEDIA_QUERY.matches &&
    video.closest('.comp-homepage-hero--has-mobile-media') !== null
  );
}

/**
 * @param {HTMLVideoElement} video
 * @param {object} initializationRun
 * @returns {boolean}
 */
function isCurrentInitialization(video, initializationRun) {
  return (
    video.isConnected &&
    initializedVideos.has(video) &&
    initializationRuns.get(video) === initializationRun &&
    !isHomepageHeroMobileMediaActive(video)
  );
}

/**
 * Stop playback and release any network-backed media resources.
 *
 * @param {HTMLVideoElement} video
 * @param {boolean} [resetInitialization]
 */
function clearFlowplayerPlayback(video, resetInitialization = false) {
  const hls = hlsPlayers.get(video);
  const nativeErrorHandler = nativePlaybackErrorHandlers.get(video);

  if (hls) {
    hls.destroy();
    hlsPlayers.delete(video);
  }

  if (nativeErrorHandler) {
    video.removeEventListener('error', nativeErrorHandler);
    nativePlaybackErrorHandlers.delete(video);
  }

  video.pause();

  if (video.hasAttribute('src') || video.currentSrc) {
    video.removeAttribute('src');
    video.load();
  }

  if (resetInitialization) {
    initializedVideos.delete(video);
    initializationRuns.delete(video);
  }
}

/**
 * Reveal the component's image fallback when playback cannot be recovered.
 *
 * @param {HTMLVideoElement} video
 */
function showFlowplayerFallback(video) {
  const container = video.closest('.comp-homepage-hero, .is-flowplayer-video, .is-flowplayer-video-bg');
  clearFlowplayerPlayback(video);
  video.removeAttribute('autoplay');

  const hasFallback =
    container?.classList.contains('comp-homepage-hero') ||
    container?.querySelector('.comp-textpic__video-fallback, .comp-textpic__video-fallback-picture');

  if (hasFallback) {
    container?.classList.add('has-flowplayer-error');
  }
}

/**
 * Request muted autoplay without leaving an unhandled play promise in browsers
 * where the visitor has explicitly disabled all autoplay.
 *
 * @param {HTMLVideoElement} video
 * @param {object} initializationRun
 */
async function autoplay(video, initializationRun) {
  try {
    await video.play();
  } catch (error) {
    if (
      !isCurrentInitialization(video, initializationRun) ||
      (error instanceof DOMException && error.name === 'AbortError')
    ) {
      return;
    }

    showFlowplayerFallback(video);
  }
}

/**
 * Initialize native HLS playback in Safari and other browsers that support it.
 *
 * @param {HTMLVideoElement} video
 * @param {string} masterUrl
 * @param {object} initializationRun
 */
function initNativePlayback(video, masterUrl, initializationRun) {
  const handleError = () => {
    nativePlaybackErrorHandlers.delete(video);

    if (isCurrentInitialization(video, initializationRun)) {
      showFlowplayerFallback(video);
    }
  };

  nativePlaybackErrorHandlers.set(video, handleError);
  video.addEventListener('error', handleError, { once: true });
  video.src = masterUrl;
  video.load();
  autoplay(video, initializationRun);
}

/**
 * Initialize Media Source Extensions playback for Firefox and other browsers
 * without native HLS support.
 *
 * @param {HTMLVideoElement} video
 * @param {string} masterUrl
 * @param {HlsConstructor} Hls
 * @param {object} initializationRun
 */
function initHlsPlayback(video, masterUrl, Hls, initializationRun) {
  const hls = new Hls();
  let networkRecoveryAttempted = false;
  let mediaRecoveryAttempted = false;

  hlsPlayers.set(video, hls);

  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    if (isCurrentInitialization(video, initializationRun)) {
      autoplay(video, initializationRun);
    }
  });
  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (!isCurrentInitialization(video, initializationRun) || !data.fatal) {
      return;
    }

    if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !networkRecoveryAttempted) {
      networkRecoveryAttempted = true;
      hls.startLoad();
      return;
    }

    if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !mediaRecoveryAttempted) {
      mediaRecoveryAttempted = true;
      hls.recoverMediaError();
      return;
    }

    showFlowplayerFallback(video);
  });

  hls.loadSource(masterUrl);
  hls.attachMedia(video);
}

/**
 * @param {HTMLVideoElement} video
 */
async function initFlowplayerVideo(video) {
  if (
    initializedVideos.has(video) ||
    isHomepageHeroMobileMediaActive(video) ||
    video.closest('.has-flowplayer-error')
  ) {
    return;
  }

  initializedVideos.add(video);
  const initializationRun = {};
  initializationRuns.set(video, initializationRun);

  const masterUrl = video.dataset.flowplayerMasterUrl;
  const hlsLibraryUrl = video.dataset.hlsLibraryUrl;

  if (!masterUrl) {
    showFlowplayerFallback(video);
    return;
  }

  // Set both the default and current muted states before requesting autoplay.
  video.defaultMuted = true;
  video.muted = true;

  if (video.canPlayType(HLS_MIME_TYPE)) {
    initNativePlayback(video, masterUrl, initializationRun);
    return;
  }

  if (!hlsLibraryUrl) {
    showFlowplayerFallback(video);
    return;
  }

  try {
    const Hls = await loadHlsLibrary(hlsLibraryUrl);

    if (!isCurrentInitialization(video, initializationRun)) {
      return;
    }

    if (!Hls.isSupported()) {
      showFlowplayerFallback(video);
      return;
    }

    initHlsPlayback(video, masterUrl, Hls, initializationRun);
  } catch (error) {
    if (isCurrentInitialization(video, initializationRun)) {
      showFlowplayerFallback(video);
    }
  }
}

/**
 * @param {ParentNode} root
 */
function syncFlowplayerVideos(root = document) {
  root.querySelectorAll(FLOWPLAYER_VIDEO_SELECTOR).forEach((element) => {
    if (element instanceof HTMLVideoElement) {
      if (isHomepageHeroMobileMediaActive(element)) {
        if (initializedVideos.has(element)) {
          clearFlowplayerPlayback(element, true);
        }
      } else {
        initFlowplayerVideo(element);
      }
    }
  });
}

syncFlowplayerVideos();

const handleHomepageHeroBreakpointChange = () => syncFlowplayerVideos();

if (typeof HOMEPAGE_HERO_MOBILE_MEDIA_QUERY.addEventListener === 'function') {
  HOMEPAGE_HERO_MOBILE_MEDIA_QUERY.addEventListener('change', handleHomepageHeroBreakpointChange);
} else {
  HOMEPAGE_HERO_MOBILE_MEDIA_QUERY.addListener(handleHomepageHeroBreakpointChange);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    syncFlowplayerVideos(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof HTMLElement)) {
    return;
  }

  event.target.querySelectorAll(FLOWPLAYER_VIDEO_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLVideoElement)) {
      return;
    }

    clearFlowplayerPlayback(element, true);
  });
});
